import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { RoomData } from '@/AppRedux/actions/admin/roomDetailConfig/roomDetailConfigType';
import {
  roomDetailsListRequest,
  roomDetailsUpdateRequest,
  roomDetailsUpdateSortOrderRequest,
} from '@/AppRedux/actions/admin/roomDetailConfig';
import { useAppDispatch, useAppSelector } from '@/AppRedux/store/hooks';

import { FlattenedRoomData } from './components/types';
import { useResponsive } from '@/hooks/useResponsive';
import { useSortOrder } from '@/hooks/useSortOrder';
import RoomSectionHeader from './components/RoomSectionHeader';
import RoomDesktopTable from './components/RoomDesktopTable';
import RoomTabletTable from './components/RoomTabletTable';
import RoomMobileList from './components/RoomMobileList';
import RoomDetailModal from './components/RoomDetailModal';
import SortOrderActionBar from './components/SortOrderActionBar';

const RoomDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { dataSource, loading, process } = useAppSelector((state) => state.roomDetailConfig);
  const rooms = dataSource || [];

  const [selectedRoom, setSelectedRoom] = useState<RoomData | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updatingRoomCode, setUpdatingRoomCode] = useState<string | null>(null);

  const useCurrentFirstRender = useRef(true);

  const { isMobile, isTablet, isDesktop } = useResponsive();

  useEffect(() => {
    if (useCurrentFirstRender.current) {
      dispatch(roomDetailsListRequest());
      useCurrentFirstRender.current = false;
    }
  }, [dispatch]);

  const flattenedRooms: FlattenedRoomData[] = useMemo(() => {
    return rooms.map(room => {
      const defaultContent = room.contents[0] || {};
      return { ...room, ...defaultContent };
    }).filter(room => room.contents && room.contents.length > 0);
  }, [rooms]);

  const {
    isSortMode,
    isDirty,
    displayItems: displayRooms,
    computeDiff,
    enterSortMode,
    cancelSortMode,
    handleDragEnd,
    handleMoveUp,
    handleMoveDown,
  } = useSortOrder(flattenedRooms, (r) => r.room_code);

  useEffect(() => {
    if (process.action === 'ROOM_DETAILS_UPDATE_REQUEST') {
      if (process.status) {
        message.success('Room updated successfully!');
        dispatch(roomDetailsListRequest());
      } else {
        Modal.error({ title: 'Error', content: process.message || 'Failed to update room.' });
      }
      setUpdatingRoomCode(null);
    }
  }, [process, dispatch]);

  useEffect(() => {
    if (process.action === 'ROOM_DETAILS_UPDATE_SORT_ORDER_REQUEST') {
      if (process.status) {
        message.success('Sort order updated successfully!');
        dispatch(roomDetailsListRequest());
        cancelSortMode();
      } else {
        Modal.error({ title: 'Error', content: process.message || 'Failed to update sort order.' });
      }
    }
  }, [process, dispatch, cancelSortMode]);

  const findRoomByCode = (roomCode: string) => rooms.find((room) => room.room_code === roomCode) || null;

  const showRoomDetail = (room: FlattenedRoomData) => {
    const originalRoom = findRoomByCode(room.room_code);
    setSelectedRoom(originalRoom);
    setIsModalVisible(true);
  };

  const handleCancelModal = () => {
    setIsModalVisible(false);
    setSelectedRoom(null);
  };

  const handleToggleRoomEnabled = (record: FlattenedRoomData, checked: boolean) => {
    const originalRoom = findRoomByCode(record.room_code);
    if (!originalRoom) {
      message.error("Could not find room data to update.");
      return;
    }
    setUpdatingRoomCode(record.room_code);
    const updatedRoomData: RoomData = { ...originalRoom, enabled: checked };
    dispatch(roomDetailsUpdateRequest(updatedRoomData));
  };

  const handleEditRoom = (record: FlattenedRoomData) => {
    navigate(`./${encodeURIComponent(record.room_code)}/edit`);
  };

  const handleCreateRoom = () => {
    navigate(`./create`);
  };

  const handleSaveSortOrder = () => {
    const diff = computeDiff();
    if (diff.length === 0) return;
    dispatch(roomDetailsUpdateSortOrderRequest(
      diff.map((d) => ({ room_id: d.id, sort_order: d.sort_order }))
    ));
  };

  const isSortSaving = loading && process.action === 'ROOM_DETAILS_UPDATE_SORT_ORDER_REQUEST' as any;

  const commonProps = {
    rooms: displayRooms,
    loading: loading && !process.status,
    updatingRoomCode,
    isSortMode,
    onShowDetail: showRoomDetail,
    onEdit: handleEditRoom,
    onToggleEnabled: handleToggleRoomEnabled,
    onDragEnd: handleDragEnd,
  };

  return (
    <Card>
      <RoomSectionHeader
        onCreateRoom={handleCreateRoom}
        isSortMode={isSortMode}
        onToggleSortMode={isSortMode ? cancelSortMode : enterSortMode}
      />

      {isDesktop && <RoomDesktopTable {...commonProps} />}
      {isTablet && <RoomTabletTable {...commonProps} />}
      {isMobile && (
        <RoomMobileList
          rooms={displayRooms}
          loading={loading && !process.status}
          updatingRoomCode={updatingRoomCode}
          isSortMode={isSortMode}
          onShowDetail={showRoomDetail}
          onEdit={handleEditRoom}
          onToggleEnabled={handleToggleRoomEnabled}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
        />
      )}

      {isSortMode && (
        <SortOrderActionBar
          isDirty={isDirty}
          loading={isSortSaving}
          isMobile={isMobile}
          onCancel={cancelSortMode}
          onSave={handleSaveSortOrder}
        />
      )}

      <RoomDetailModal
        room={selectedRoom}
        visible={isModalVisible}
        onClose={handleCancelModal}
      />
    </Card>
  );
};

export default RoomDetails;
